import { Component, h } from 'preact'
import { connectStateResults } from 'react-instantsearch-dom'
import _ from 'underscore'
import { insights } from '../../services/algoliaService'
import Phrase from '../../libs/translationsUtils/phrase'
import tracker from '../../components/tracker/publish'
import { t } from '../../libs/translationsUtils/translations'
import localStorageCache from '../../utils/localStorageCache'
import { SEARCH_CONVERSION } from '../../libs/config'
import {
  SearchResults,
  AllSearchResults,
  isZendeskResult,
  ZendeskHit,
  SkilljarHit,
  FreshdeskHit,
  isCommunityResult,
  Hit,
  EventHit,
  isSalesforceResult, SalesforceHit,
  isSkilljarResult,
  isFreshdeskResult
} from '../algolia/components'

import CommunitySearchResults from './CommunitySearchResults'
import ZendeskSearchResults from './ZendeskSearchResults'
import SalesforceSearchResults from './SalesforceSearchResults'
import SkilljarSearchResults from './SkilljarSearchResults'
import FreshdeskSearchResults from './FreshdeskSearchResults'
import { common } from '../common'

const eventTracker = () => {
  tracker.trackEvent('userevent', {
    type: 'Topic Initiated',
    trigger: 'widget',
    source: 'search',
    user_role: window.inSidedData.user.role,
    button_label: t('Forum', 'main.navigation.add_topic')
  })
}

interface Props {
  isGuest: boolean
  isZendeskFederatedSearch: boolean
  isFederatedSalesforceSearch: boolean
  isFederatedSkilljarSearch: boolean
  isFederatedFreshdeskSearch: boolean
  enableLoader: boolean
  attachmentCdn: string
  defaultCdn: string
  communityId: string
  pageCount: number
  results: Object
  loaderCallback: (enableLoader: boolean) => void
  loadNextPage: () => void
  searchResults: SearchResults
  allSearchResults: AllSearchResults
  totalTopicsAmount: (topicAmounts: {community: number, zendesk: number, salesforce: number, skilljar: number, freshdesk: number, totalTopics: number}) => void
}

class SearchResultsContainer extends Component<Props> {
  constructor (props: Props) {
    super(props)

    this.getResultsToDisplay = this.getResultsToDisplay.bind(this)
    this.getZendeskHits = this.getZendeskHits.bind(this)
    this.getSalesforceHits = this.getSalesforceHits.bind(this)
    this.getSkilljarHits = this.getSkilljarHits.bind(this)
    this.getFreshdeskHits = this.getFreshdeskHits.bind(this)
    this.sendClickAnalytic = this.sendClickAnalytic.bind(this)
    this.getCommunityHitForClick = this.getCommunityHitForClick.bind(this)
    this.checkForScrollPosition = _.debounce(this.checkForScrollPosition.bind(this), 150)
  }

  getResultsToDisplay (props: Props) {
    const allSearchResults = props.allSearchResults
    if (!props.isZendeskFederatedSearch && !props.isFederatedSalesforceSearch && !props.isFederatedSkilljarSearch && !props.isFederatedFreshdeskSearch) {
      return {
        communityResults: props.searchResults,
        zendeskResults: undefined,
        salesforceResults: undefined,
        skilljarResults: undefined,
        freshdeskResults: undefined
      }
    }
    return {
      communityResults: allSearchResults?.[this.props.communityId],
      zendeskResults: allSearchResults?.[`${this.props.communityId}-zendesk`],
      salesforceResults: allSearchResults?.[`${this.props.communityId}-salesforce`],
      skilljarResults: allSearchResults?.[`${this.props.communityId}-skilljar`],
      freshdeskResults: allSearchResults?.[`${this.props.communityId}-freshdesk`]
    }
  }

  getZendeskHits (): ZendeskHit[] {
    return this.getResultsToDisplay(this.props).zendeskResults?.hits.filter(isZendeskResult) ?? []
  }

  getSalesforceHits (): SalesforceHit[] {
    return this.getResultsToDisplay(this.props).salesforceResults?.hits.filter(isSalesforceResult) ?? []
  }

  getSkilljarHits (): SkilljarHit[] {
    return this.getResultsToDisplay(this.props).skilljarResults?.hits.filter(isSkilljarResult) ?? []
  }

  getFreshdeskHits (): FreshdeskHit[] {
    return this.getResultsToDisplay(this.props).freshdeskResults?.hits.filter(isFreshdeskResult) ?? []
  }

  getSalesforceNumberOfHits () {
    return this.getResultsToDisplay(this.props).salesforceResults?.nbHits ?? 0
  }

  getZendeskNumberOfHits () {
    return this.getResultsToDisplay(this.props).zendeskResults?.nbHits ?? 0
  }

  getSkilljarNumberOfHits () {
    return this.getResultsToDisplay(this.props).skilljarResults?.nbHits ?? 0
  }

  getFreshdeskNumberOfHits () {
    return this.getResultsToDisplay(this.props).freshdeskResults?.nbHits ?? 0
  }

  getCommunityHits (): Array<Hit | EventHit> {
    return this.getResultsToDisplay(this.props).communityResults?.hits.filter(isCommunityResult) ?? []
  }

  getCommunityNumberOfHits () {
    return this.getResultsToDisplay(this.props).communityResults?.nbHits ?? 0
  }

  hasMorePages (results: SearchResults) {
    return this.props.pageCount < results.nbPages - 1
  }

  canLoadMore (): boolean {
    const results = this.getResultsToDisplay(this.props)
    const salesforceHasMore = results.salesforceResults ? this.hasMorePages(results.salesforceResults) : false
    const zendeskHasMore = results.zendeskResults ? this.hasMorePages(results.zendeskResults) : false
    const skilljarHasMore = results.skilljarResults ? this.hasMorePages(results.skilljarResults) : false
    const freshdeskHasMore = results.freshdeskResults ? this.hasMorePages(results.freshdeskResults) : false
    const communityHasMore = results.communityResults ? this.hasMorePages(results.communityResults) : false

    return zendeskHasMore || communityHasMore || salesforceHasMore || skilljarHasMore || freshdeskHasMore
  }

  checkForScrollPosition () {
    if (!this.canLoadMore()) return

    if (this.props.enableLoader) return

    const topicItemSelector = common.isModuleActive('topicItemView') ? 'topic-view' : 'thread-list-item'

    const searchResults = document.getElementsByClassName(topicItemSelector)

    const itemToTriggerPagination = searchResults[searchResults.length - 3]
    if (!itemToTriggerPagination) return

    const itemToTriggerPaginationBoundaries = itemToTriggerPagination.getBoundingClientRect()

    if (window.innerHeight > itemToTriggerPaginationBoundaries.y) {
      this.props.loadNextPage()
    }
  }

  getCommunityHitForClick (topicId: number, idx: number, queryID: string) {
    const clickedHit = this.getCommunityHits().filter(hit => hit.id === topicId)[0]

    if (clickedHit) {
      this.sendClickAnalytic(clickedHit, idx, queryID, this.props.communityId)
    }
  }

  sendClickAnalytic (item: EventHit | Hit | ZendeskHit | SalesforceHit | SkilljarHit | FreshdeskHit, idx: number, queryID: string, indexName: string) {
    insights('clickedObjectIDsAfterSearch', {
      eventName: 'Search page item click',
      objectIDs: [item.objectID],
      positions: [idx + 1],
      queryID: queryID,
      index: indexName
    })

    localStorageCache.setItem(SEARCH_CONVERSION, {
      objectIDs: [item.objectID],
      queryID: queryID,
      index: indexName
    })
  }

  componentDidMount () {
    document.addEventListener('scroll', this.checkForScrollPosition)
  }

  componentWillUnmount () {
    document.removeEventListener('scroll', this.checkForScrollPosition)
  }

  render ({ isGuest, searchResults, attachmentCdn, defaultCdn, enableLoader, pageCount, totalTopicsAmount }: Props) {
    const totalTopics = this.getCommunityNumberOfHits() + this.getZendeskNumberOfHits() + this.getSalesforceNumberOfHits() + this.getSkilljarNumberOfHits() + this.getFreshdeskNumberOfHits()

    totalTopicsAmount({
      totalTopics: totalTopics,
      zendesk: this.getZendeskNumberOfHits(),
      community: this.getCommunityNumberOfHits(),
      salesforce: this.getSalesforceNumberOfHits(),
      skilljar: this.getSkilljarNumberOfHits(),
      freshdesk: this.getFreshdeskNumberOfHits()
    })

    return (
      <div>
        <div className='list__items--topic-count'>{totalTopics} <Phrase p='Topic|Topics' m='Forum' count={{ value: totalTopics.toString() }} /></div>
        {
          totalTopics === 0
            ? (
              <div
                className='thread-list-block thread-list-block-empty-status thread-list-block--meta forum-list-view-spacing forum-list-view-spacing--v-s'
              >
                <p className='p--full-width'>
                  <Phrase p='searchresults.none.first' m='Forum' /> <strong>{searchResults?.query}</strong>.
                  <Phrase p='searchresults.none.last' m='Forum' />
                </p>

                <a
                  href='/topic/new'
                  className='btn--cta btn--new-topic qa-topic-new-button'
                  onClick={() => eventTracker()}
                >
                  <Phrase p='main.navigation.add_topic' m='Forum' />
                </a>
              </div>
              )
            : (
              <div className='search-topic-list'>

                <CommunitySearchResults
                  pageCount={pageCount}
                  clickCallback={(topicId, idx) => { this.getCommunityHitForClick(topicId, idx, searchResults.queryID) }}
                  topics={this.getCommunityHits()}
                  loaderCallback={this.props.loaderCallback}
                  isGuest={isGuest}
                  attachmentCdn={attachmentCdn}
                />

                <ZendeskSearchResults
                  clickCallback={(hit, idx) => this.sendClickAnalytic(hit, idx, searchResults.queryID, `${this.props.communityId}-zendesk`)}
                  hits={this.getZendeskHits()}
                  pageCount={pageCount}
                />

                <SalesforceSearchResults
                  clickCallback={(hit, idx) => this.sendClickAnalytic(hit, idx, searchResults.queryID, `${this.props.communityId}-salesforce`)}
                  hits={this.getSalesforceHits()}
                  pageCount={pageCount}
                />

                <SkilljarSearchResults
                  clickCallback={(hit, idx) => this.sendClickAnalytic(hit, idx, searchResults.queryID, `${this.props.communityId}-skilljar`)}
                  hits={this.getSkilljarHits()}
                  pageCount={pageCount}
                />

                <FreshdeskSearchResults
                  clickCallback={(hit, idx) => this.sendClickAnalytic(hit, idx, searchResults.queryID, `${this.props.communityId}-freshdesk`)}
                  hits={this.getFreshdeskHits()}
                  pageCount={pageCount}
                />
              </div>
              )
        }
        {
          enableLoader && pageCount !== 0
            ? (
              <div className='instant-search-loader_pagination'>
                <img
                  src={`${defaultCdn}/images/spinner.gif`}
                  alt='loading...'
                />
              </div>
              )
            : null
        }
      </div>
    )
  }
}

const SearchResultsWithMetadata = connectStateResults(SearchResultsContainer)

export default SearchResultsWithMetadata
